import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Grid,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Alert,
  CircularProgress,
  InputAdornment,
  Tooltip,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  SelectChangeEvent
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Info as InfoIcon,
  CurrencyExchange as CurrencyExchangeIcon,
  Percent as PercentIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import { useNavigate, useParams, Link } from 'react-router-dom';
import apiService from '../../../services/apiService';

// واجهة عنصر الرسوم
interface Fee {
  id: string;
  name: string;
  type: 'fixed' | 'percentage' | 'tax';
  value: number;
}

// واجهة مجموعة الرسوم
interface FeeGroupFormData {
  name: string;
  description?: string;
  fees: Fee[];
}

// واجهة حوار تحرير الرسوم
interface FeeDialogData {
  id: string;
  name: string;
  type: 'fixed' | 'percentage' | 'tax';
  value: number;
}

const FeeGroupForm: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const isEditMode = Boolean(id);
  const navigate = useNavigate();
  
  // حالة النموذج
  const [formData, setFormData] = useState<FeeGroupFormData>({
    name: '',
    description: '',
    fees: []
  });
  
  // حالة التحقق من النموذج
  const [formErrors, setFormErrors] = useState<{
    name?: string;
    fees?: string;
  }>({});
  
  // حالة التحميل والخطأ
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // حالة حوار إضافة/تحرير الرسوم
  const [feeDialogOpen, setFeeDialogOpen] = useState<boolean>(false);
  const [feeFormData, setFeeFormData] = useState<FeeDialogData>({
    id: '',
    name: '',
    type: 'fixed',
    value: 0
  });
  const [feeFormErrors, setFeeFormErrors] = useState<{
    name?: string;
    value?: string;
  }>({});
  const [feeEditIndex, setFeeEditIndex] = useState<number | null>(null);
  
  // تحميل البيانات الأولية
  useEffect(() => {
    if (isEditMode && id) {
      fetchFeeGroupData(id);
    }
  }, [id, isEditMode]);
  
  // جلب بيانات مجموعة الرسوم للتحرير
  const fetchFeeGroupData = async (groupId: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiService.get<FeeGroupFormData>(`/finance/accounts/fee-groups/${groupId}`);
      
      // تحديث النموذج ببيانات المجموعة
      setFormData(response);
      
      setLoading(false);
    } catch (err: any) {
      console.error('خطأ في تحميل بيانات مجموعة الرسوم:', err);
      setError(err.response?.data?.message || 'حدث خطأ في تحميل بيانات مجموعة الرسوم');
      setLoading(false);
    }
  };
  
  // التعامل مع تغييرات النموذج للحقول النصية
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // مسح رسالة الخطأ للحقل
    if (formErrors[name as keyof typeof formErrors]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name as keyof typeof newErrors];
        return newErrors;
      });
    }
  };
  
  // التعامل مع تغييرات حوار الرسوم للحقول النصية
  const handleFeeInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFeeFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // مسح رسالة الخطأ للحقل
    if (feeFormErrors[name as keyof typeof feeFormErrors]) {
      setFeeFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name as keyof typeof newErrors];
        return newErrors;
      });
    }
  };
  
  // التعامل مع تغييرات حوار الرسوم للقيمة الرقمية
  const handleFeeValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(event.target.value);
    setFeeFormData(prev => ({
      ...prev,
      value: isNaN(value) ? 0 : value
    }));
    
    // مسح رسالة الخطأ للحقل
    if (feeFormErrors.value) {
      setFeeFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.value;
        return newErrors;
      });
    }
  };
  
  // التعامل مع تغييرات حوار الرسوم للقائمة المنسدلة
  const handleFeeTypeChange = (event: SelectChangeEvent) => {
    const { value } = event.target;
    setFeeFormData(prev => ({
      ...prev,
      type: value as 'fixed' | 'percentage' | 'tax'
    }));
  };
  
  // فتح حوار إضافة رسم جديد
  const handleAddFee = () => {
    setFeeFormData({
      id: `fee-${Date.now()}`,
      name: '',
      type: 'fixed',
      value: 0
    });
    setFeeFormErrors({});
    setFeeEditIndex(null);
    setFeeDialogOpen(true);
  };
  
  // فتح حوار تحرير رسم موجود
  const handleEditFee = (index: number) => {
    const fee = formData.fees[index];
    setFeeFormData({
      id: fee.id,
      name: fee.name,
      type: fee.type,
      value: fee.value
    });
    setFeeFormErrors({});
    setFeeEditIndex(index);
    setFeeDialogOpen(true);
  };
  
  // حذف رسم
  const handleDeleteFee = (index: number) => {
    setFormData(prev => ({
      ...prev,
      fees: prev.fees.filter((_, i) => i !== index)
    }));
  };
  
  // التحقق من نموذج الرسوم
  const validateFeeForm = (): boolean => {
    const errors: {
      name?: string;
      value?: string;
    } = {};
    
    if (!feeFormData.name.trim()) {
      errors.name = 'يجب إدخال اسم الرسم';
    }
    
    if (feeFormData.value <= 0) {
      errors.value = 'يجب أن تكون قيمة الرسم أكبر من صفر';
    }
    
    setFeeFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // حفظ الرسم في مجموعة الرسوم
  const handleSaveFee = () => {
    if (!validateFeeForm()) {
      return;
    }
    
    const newFee: Fee = {
      id: feeFormData.id,
      name: feeFormData.name.trim(),
      type: feeFormData.type,
      value: feeFormData.value
    };
    
    if (feeEditIndex !== null) {
      // تحديث رسم موجود
      setFormData(prev => {
        const updatedFees = [...prev.fees];
        updatedFees[feeEditIndex] = newFee;
        return {
          ...prev,
          fees: updatedFees
        };
      });
    } else {
      // إضافة رسم جديد
      setFormData(prev => ({
        ...prev,
        fees: [...prev.fees, newFee]
      }));
    }
    
    // إغلاق الحوار
    setFeeDialogOpen(false);
  };
  
  // الحصول على أيقونة نوع الرسم
  const getFeeTypeIcon = (type: 'fixed' | 'percentage' | 'tax') => {
    switch (type) {
      case 'fixed': return <CurrencyExchangeIcon />;
      case 'percentage': return <PercentIcon />;
      case 'tax': return <ReceiptIcon />;
      default: return <CurrencyExchangeIcon />;
    }
  };
  
  // الحصول على نص نوع الرسم
  const getFeeTypeText = (type: 'fixed' | 'percentage' | 'tax'): string => {
    switch (type) {
      case 'fixed': return 'مبلغ ثابت';
      case 'percentage': return 'نسبة مئوية';
      case 'tax': return 'ضريبة';
      default: return '';
    }
  };
  
  // تنسيق قيمة الرسم للعرض
  const formatFeeValue = (type: 'fixed' | 'percentage' | 'tax', value: number): string => {
    if (type === 'fixed') {
      return value.toLocaleString('ar-SA', { style: 'currency', currency: 'SAR' });
    } else {
      return `${value}%`;
    }
  };
  
  // التحقق من النموذج الرئيسي
  const validateForm = (): boolean => {
    const errors: {
      name?: string;
      fees?: string;
    } = {};
    
    if (!formData.name.trim()) {
      errors.name = 'يجب إدخال اسم المجموعة';
    }
    
    if (formData.fees.length === 0) {
      errors.fees = 'يجب إضافة رسم واحد على الأقل للمجموعة';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // إرسال النموذج
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitting(true);
    setError(null);
    
    try {
      if (isEditMode && id) {
        // تحديث مجموعة موجودة
        await apiService.put(`/finance/accounts/fee-groups/${id}`, formData);
      } else {
        // إنشاء مجموعة جديدة
        await apiService.post('/finance/accounts/fee-groups', formData);
      }
      
      // العودة إلى قائمة مجموعات الرسوم
      navigate('/finance/accounts/fee-groups');
    } catch (err: any) {
      console.error('خطأ في حفظ مجموعة الرسوم:', err);
      setError(err.response?.data?.message || 'حدث خطأ في حفظ مجموعة الرسوم');
      setSubmitting(false);
    }
  };
  
  // عنوان الصفحة
  const pageTitle = isEditMode ? 'تعديل مجموعة رسوم' : 'إنشاء مجموعة رسوم جديدة';
  
  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={2} sx={{ p: 3 }}>
        {/* رأس الصفحة */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              component={Link}
              to="/finance/accounts/fee-groups"
              sx={{ mr: 1 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" component="h1">
              {pageTitle}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<CancelIcon />}
              href="/finance/accounts/fee-groups"
              disabled={submitting}
            >
              إلغاء
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={submitting ? <CircularProgress size={20} /> : <SaveIcon />}
              onClick={handleSubmit}
              disabled={submitting}
            >
              حفظ
            </Button>
          </Box>
        </Box>
        
        {/* رسائل الخطأ */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 3 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={40} />
          </Box>
        ) : (
          <Box component="form">
            <Grid container spacing={3}>
              {/* المعلومات الأساسية */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2, color: 'text.primary', fontWeight: 500 }}>
                  معلومات المجموعة
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="اسم المجموعة"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      error={!!formErrors.name}
                      helperText={formErrors.name}
                      required
                      placeholder="أدخل اسمًا وصفيًا للمجموعة"
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="وصف المجموعة (اختياري)"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      multiline
                      rows={2}
                      placeholder="أدخل وصفًا تفصيليًا للمجموعة ليساعد المستخدمين على فهم الغرض منها"
                    />
                  </Grid>
                </Grid>
              </Grid>
              
              {/* تفاصيل الرسوم */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, mt: 2 }}>
                  <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 500 }}>
                    تفاصيل الرسوم
                  </Typography>
                  
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleAddFee}
                  >
                    إضافة رسم جديد
                  </Button>
                </Box>
                <Divider sx={{ mb: 2 }} />
                
                {formErrors.fees && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {formErrors.fees}
                  </Alert>
                )}
                
                {formData.fees.length === 0 ? (
                  <Card variant="outlined" sx={{ mb: 2 }}>
                    <CardContent sx={{ textAlign: 'center', py: 4 }}>
                      <InfoIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                      <Typography variant="body1" color="text.secondary">
                        لم يتم إضافة أي رسوم لهذه المجموعة بعد.
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        انقر على زر "إضافة رسم جديد" لإضافة رسوم لهذه المجموعة.
                      </Typography>
                    </CardContent>
                  </Card>
                ) : (
                  <List>
                    {formData.fees.map((fee, index) => (
                      <ListItem
                        key={fee.id}
                        sx={{
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          py: 1.5,
                          '&:last-child': { border: 'none' }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                          {getFeeTypeIcon(fee.type)}
                        </Box>
                        <ListItemText
                          primary={fee.name}
                          secondary={
                            <Box component="span" sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                              <Typography variant="body2" component="span" color="text.secondary">
                                {getFeeTypeText(fee.type)}
                              </Typography>
                              <Typography variant="body2" component="span" color="primary" sx={{ mx: 1 }}>
                                {formatFeeValue(fee.type, fee.value)}
                              </Typography>
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <Tooltip title="تحرير">
                            <IconButton edge="end" aria-label="تحرير" onClick={() => handleEditFee(index)} sx={{ mr: 1 }}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="حذف">
                            <IconButton edge="end" aria-label="حذف" onClick={() => handleDeleteFee(index)}>
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                )}
                
                <Box sx={{ mt: 2 }}>
                  <Alert severity="info">
                    <Typography variant="body2">
                      <strong>ملاحظة:</strong> عند تطبيق مجموعة رسوم على سند، يتم خصم الرسوم فقط من المبلغ الداخل إلى الحساب المدين، بينما الحساب الدائن لا يتأثر بهذه الخصومات.
                    </Typography>
                  </Alert>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>
      
      {/* حوار إضافة/تحرير الرسوم */}
      <Dialog open={feeDialogOpen} onClose={() => setFeeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {feeEditIndex !== null ? 'تحرير الرسم' : 'إضافة رسم جديد'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="اسم الرسم"
                  name="name"
                  value={feeFormData.name}
                  onChange={handleFeeInputChange}
                  error={!!feeFormErrors.name}
                  helperText={feeFormErrors.name}
                  required
                  placeholder="مثال: رسوم المعاملة، ضريبة القيمة المضافة، إلخ."
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>نوع الرسم</InputLabel>
                  <Select
                    name="type"
                    value={feeFormData.type}
                    label="نوع الرسم"
                    onChange={handleFeeTypeChange}
                  >
                    <MenuItem value="fixed">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CurrencyExchangeIcon sx={{ mr: 1 }} />
                        مبلغ ثابت
                      </Box>
                    </MenuItem>
                    <MenuItem value="percentage">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PercentIcon sx={{ mr: 1 }} />
                        نسبة مئوية
                      </Box>
                    </MenuItem>
                    <MenuItem value="tax">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ReceiptIcon sx={{ mr: 1 }} />
                        ضريبة
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="القيمة"
                  name="value"
                  type="number"
                  value={feeFormData.value}
                  onChange={handleFeeValueChange}
                  error={!!feeFormErrors.value}
                  helperText={feeFormErrors.value}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {feeFormData.type === 'fixed' ? 'ر.س' : '%'}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mt: 1 }}>
                  {feeFormData.type === 'fixed' && (
                    <Typography variant="body2">
                      سيتم خصم مبلغ ثابت قدره {feeFormData.value.toLocaleString('ar-SA')} ر.س من المبلغ الإجمالي.
                    </Typography>
                  )}
                  {feeFormData.type === 'percentage' && (
                    <Typography variant="body2">
                      سيتم خصم نسبة قدرها {feeFormData.value}% من المبلغ الإجمالي.
                    </Typography>
                  )}
                  {feeFormData.type === 'tax' && (
                    <Typography variant="body2">
                      سيتم تطبيق ضريبة بنسبة {feeFormData.value}% على مجموع الرسوم الثابتة والنسبية الأخرى.
                    </Typography>
                  )}
                </Alert>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFeeDialogOpen(false)} color="inherit">
            إلغاء
          </Button>
          <Button onClick={handleSaveFee} color="primary" variant="contained">
            حفظ
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FeeGroupForm;